<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const store = useStore();
const isSubmitting = ref(false);
const dealsUIFlags = useMapGetter('deals/getUIFlags');
const agents = useMapGetter('agents/getVerifiedAgents');
const currentUserId = useMapGetter('getCurrentUserID');
const currentRole = useMapGetter('getCurrentRole');
const accountId = useMapGetter('getCurrentAccountId');

// Don't rely on some other part of the app having already fetched agents —
// this dialog needs them for the (required) agent dropdown, so make sure
// they're loaded here too rather than risking an empty, unsubmittable form.
onMounted(() => {
  if (!agents.value.length) store.dispatch('agents/get');
});

const dialogRef = ref(null);
const editingDealId = ref(null);
const editingDealOwnerId = ref(null);
const editingDealContactId = ref(null);
const isEditing = computed(() => editingDealId.value !== null);

const contactUrl = computed(() =>
  editingDealContactId.value
    ? frontendURL(
        `accounts/${accountId.value}/contacts/${editingDealContactId.value}`
      )
    : null
);

// Agents may only edit/delete deals they created themselves (enforced
// server-side too -- this just avoids letting someone fill out a form
// they can't actually save, or see actions they'd just get a 403 on).
const canManage = computed(
  () =>
    !isEditing.value ||
    currentRole.value === 'administrator' ||
    editingDealOwnerId.value === currentUserId.value
);

const fieldClass =
  'w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand';

const defaultForm = () => ({
  type: 'Listing',
  stage: 'pre',
  address: '',
  city: '',
  state: 'OH',
  website: '',
  client: '',
  agent: '',
  beds: 3,
  price: '',
  gci: '',
  rent: '',
  keyDate: '',
  notes: '',
});

const form = ref(defaultForm());

const isLease = computed(() => form.value.type === 'Lease');

const saleStageOptions = [
  { id: 'pre', label: t('DEAL_BOARD.STAGES.PRE_LISTING') },
  { id: 'active', label: t('DEAL_BOARD.STAGES.ACTIVE') },
  { id: 'uc', label: t('DEAL_BOARD.STAGES.UNDER_CONTRACT') },
  { id: 'closed', label: t('DEAL_BOARD.STAGES.CLOSED') },
];

const leaseStageOptions = [
  { id: 'vacant', label: t('DEAL_BOARD.STAGES.VACANT') },
  { id: 'leased', label: t('DEAL_BOARD.STAGES.LEASED') },
];

const onTypeChange = () => {
  form.value.stage = isLease.value ? 'vacant' : 'pre';
};

// `deal` uses the same shape as Index.vue's `allDeals` (camelCase, city
// stored as "City, ST"). Passing one opens the dialog in edit mode.
const open = (deal = null) => {
  if (deal) {
    editingDealId.value = deal.id;
    editingDealOwnerId.value = deal.userId ?? null;
    editingDealContactId.value = deal.contactId ?? null;
    const [city = '', state = ''] = (deal.city || '').split(', ');
    form.value = {
      type: deal.type,
      stage: deal.stage,
      address: deal.address || '',
      city,
      state,
      website: deal.listingUrl || '',
      client: deal.client || '',
      agent: deal.agent || '',
      beds: deal.beds ?? 0,
      price: deal.price ?? '',
      gci: deal.gci ?? '',
      rent: deal.rent ?? '',
      keyDate: deal.keyDate ? deal.keyDate.slice(0, 7) : '',
      notes: deal.notes || '',
    };
  } else {
    editingDealId.value = null;
    editingDealOwnerId.value = null;
    editingDealContactId.value = null;
    form.value = defaultForm();
  }
  dialogRef.value?.open();
};

const normalizeUrl = url => {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const handleSubmit = async () => {
  const price = Number(form.value.price) || 0;
  // Leave commission genuinely blank when not entered, rather than
  // fabricating a flat 3% guess that would look like real user data.
  const gci = form.value.gci !== '' ? Number(form.value.gci) : null;

  const deal = {
    deal_type: form.value.type,
    stage: form.value.stage,
    address: form.value.address.trim(),
    city: `${form.value.city.trim()}, ${form.value.state.trim()}`,
    listing_url: normalizeUrl(form.value.website.trim()),
    client_name: form.value.client.trim(),
    agent_name: form.value.agent,
    beds: Number(form.value.beds) || 0,
    key_date: form.value.keyDate ? `${form.value.keyDate}-01` : null,
    notes: form.value.notes.trim() || null,
  };

  if (isLease.value) {
    deal.rent = Number(form.value.rent) || 0;
  } else {
    deal.price = price;
    deal.gci = gci;
  }

  isSubmitting.value = true;
  try {
    if (isEditing.value) {
      await store.dispatch('deals/update', { id: editingDealId.value, ...deal });
    } else {
      await store.dispatch('deals/create', deal);
    }
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t('DEAL_BOARD.DEAL_DIALOG.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const handleDelete = async () => {
  if (!editingDealId.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('DEAL_BOARD.DEAL_DIALOG.DELETE_CONFIRM'))) return;

  try {
    await store.dispatch('deals/delete', editingDealId.value);
    dialogRef.value?.close();
  } catch (error) {
    useAlert(t('DEAL_BOARD.DEAL_DIALOG.DELETE_ERROR'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditing
        ? t('DEAL_BOARD.DEAL_DIALOG.EDIT_TITLE')
        : t('DEAL_BOARD.DEAL_DIALOG.TITLE')
    "
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <form class="flex flex-col gap-3" @submit.prevent="handleSubmit">
      <div
        v-if="!canManage"
        class="rounded-lg bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11"
      >
        {{ t('DEAL_BOARD.DEAL_DIALOG.READ_ONLY_NOTICE') }}
      </div>
      <fieldset :disabled="!canManage" class="flex flex-col gap-3">
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.TYPE') }}
          </label>
          <select v-model="form.type" :class="fieldClass" @change="onTypeChange">
            <option value="Listing">{{ t('DEAL_BOARD.TYPE.LISTING') }}</option>
            <option value="Purchase">
              {{ t('DEAL_BOARD.TYPE.PURCHASE') }}
            </option>
            <option value="Lease">{{ t('DEAL_BOARD.TYPE.LEASE') }}</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.STAGE') }}
          </label>
          <select v-if="isLease" v-model="form.stage" :class="fieldClass">
            <option
              v-for="option in leaseStageOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.label }}
            </option>
          </select>
          <select v-else v-model="form.stage" :class="fieldClass">
            <option
              v-for="option in saleStageOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('DEAL_BOARD.DEAL_DIALOG.ADDRESS') }}
        </label>
        <input v-model="form.address" required :class="fieldClass" />
      </div>

      <div class="grid grid-cols-[1fr_88px] gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.CITY') }}
          </label>
          <input v-model="form.city" required :class="fieldClass" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.STATE') }}
          </label>
          <input v-model="form.state" required :class="fieldClass" />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('DEAL_BOARD.DEAL_DIALOG.WEBSITE') }}
        </label>
        <input
          v-model="form.website"
          type="text"
          :placeholder="t('DEAL_BOARD.DEAL_DIALOG.WEBSITE_PLACEHOLDER')"
          :class="fieldClass"
        />
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('DEAL_BOARD.DEAL_DIALOG.CLIENT') }}
            </label>
            <a
              v-if="contactUrl"
              :href="contactUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs font-medium text-n-blue-text hover:underline"
            >
              {{ t('DEAL_BOARD.VIEW_CONTACT') }}
            </a>
          </div>
          <input v-model="form.client" required :class="fieldClass" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.AGENT') }}
          </label>
          <select v-model="form.agent" required :class="fieldClass">
            <option value="" disabled>
              {{ t('DEAL_BOARD.DEAL_DIALOG.AGENT_PLACEHOLDER') }}
            </option>
            <option
              v-for="agent in agents"
              :key="agent.id"
              :value="agent.name"
            >
              {{ agent.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="grid grid-cols-3 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.BEDS') }}
          </label>
          <input
            v-model="form.beds"
            type="number"
            min="0"
            :class="fieldClass"
          />
        </div>
        <div v-if="!isLease" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.PRICE') }}
          </label>
          <input
            v-model="form.price"
            type="number"
            min="0"
            required
            :class="fieldClass"
          />
        </div>
        <div v-if="!isLease" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.GCI') }}
          </label>
          <input
            v-model="form.gci"
            type="number"
            min="0"
            :placeholder="t('DEAL_BOARD.DEAL_DIALOG.GCI_PLACEHOLDER')"
            :class="fieldClass"
          />
        </div>
        <div v-if="isLease" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.RENT') }}
          </label>
          <input
            v-model="form.rent"
            type="number"
            min="0"
            required
            :class="fieldClass"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('DEAL_BOARD.DEAL_DIALOG.KEY_DATE') }}
          </label>
          <input
            v-model="form.keyDate"
            type="month"
            required
            :class="fieldClass"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('DEAL_BOARD.DEAL_DIALOG.NOTES') }}
        </label>
        <textarea
          v-model="form.notes"
          rows="3"
          :placeholder="t('DEAL_BOARD.DEAL_DIALOG.NOTES_PLACEHOLDER')"
          :class="fieldClass"
        />
      </div>
      </fieldset>

      <div class="mt-2 flex items-center justify-between gap-2">
        <button
          v-if="isEditing && canManage"
          type="button"
          :disabled="dealsUIFlags.isDeleting"
          class="rounded-lg px-3.5 py-2 text-sm font-semibold text-n-ruby-9 hover:bg-n-alpha-2 disabled:opacity-50"
          @click="handleDelete"
        >
          {{ t('DEAL_BOARD.DEAL_DIALOG.DELETE') }}
        </button>
        <div class="flex flex-1 justify-end gap-2">
          <button
            type="button"
            class="rounded-lg px-3.5 py-2 text-sm font-semibold text-n-slate-11 hover:bg-n-alpha-2"
            @click="dialogRef?.close()"
          >
            {{ t('DEAL_BOARD.DEAL_DIALOG.CANCEL') }}
          </button>
          <button
            v-if="canManage"
            type="submit"
            :disabled="isSubmitting"
            class="rounded-lg bg-n-brand px-3.5 py-2 text-sm font-semibold text-white hover:enabled:brightness-110 disabled:opacity-50"
          >
            {{
              isEditing
                ? t('DEAL_BOARD.DEAL_DIALOG.EDIT_SUBMIT')
                : t('DEAL_BOARD.DEAL_DIALOG.SUBMIT')
            }}
          </button>
        </div>
      </div>
    </form>
    <template #footer />
  </Dialog>
</template>
